import { useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import {
  ROLE_DEFINITIONS,
  formatRoleDate,
  profileStatusCounts,
  rolesOf,
  todayIso,
} from "./careProfileRoles";
import { useAuth } from "contexts/AuthContext";
import careService from "services/careService";

// Designations that have a compliance policy attached. Everything else is a
// professional designation that is stored and displayed only.
const COMPLIANCE_CHECKED_CODES = ["RESIDENT_MANAGER"];

// The form is a separate component so that destroyOnClose resets its state each time
// the drawer opens.
function RolesForm({ careProfile, personName, onChange }) {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const careProfileId = careProfile?.id || careProfile?.careProfileId;

  const [selectedCode, setSelectedCode] = useState(undefined);
  const [assignedOn, setAssignedOn] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const roles = rolesOf(careProfile);
  const heldCodes = roles.map((role) => role.code);
  const availableOptions = Object.entries(ROLE_DEFINITIONS)
    .filter(([code]) => !heldCodes.includes(code))
    .map(([code, definition]) => ({ value: code, label: definition.label }));

  const holdsComplianceChecked = heldCodes.some((code) =>
    COMPLIANCE_CHECKED_CODES.includes(code),
  );

  const selectCode = (code) => {
    setSelectedCode(code);
    setAssignedOn(todayIso());
    setError("");
  };

  const clearSelection = () => {
    setSelectedCode(undefined);
    setAssignedOn(todayIso());
    setError("");
  };

  const assign = async () => {
    if (!selectedCode) {
      setError("Choose a designation to add.");
      return;
    }
    if (!assignedOn) {
      setError("Enter the date the designation started.");
      return;
    }
    if (assignedOn > todayIso()) {
      setError("The start date can't be in the future.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const updated = await careService.careProfiles.assignRole(
        careProfileId,
        selectedCode,
        { assignedOn },
        { actorId },
      );
      message.success(
        `${ROLE_DEFINITIONS[selectedCode].label} designation added.`,
      );
      clearSelection();
      onChange?.(updated);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to add the designation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    setSaving(true);
    setError("");
    try {
      const updated = await careService.careProfiles.removeRole(
        careProfileId,
        code,
        { actorId },
      );
      message.success(`${ROLE_DEFINITIONS[code].label} designation removed.`);
      onChange?.(updated);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to remove the designation.",
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = (code) => {
    const label = ROLE_DEFINITIONS[code].label;
    const name = personName || "this person";
    Modal.confirm({
      title: `Remove the ${label.toLowerCase()} designation?`,
      content: COMPLIANCE_CHECKED_CODES.includes(code)
        ? `Compliance checks will stop treating ${name} as the ${label.toLowerCase()}.`
        : `${name} will no longer be listed as ${label.toLowerCase()}.`,
      okText: "Remove designation",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: () => remove(code),
    });
  };

  return (
    <div className="care-staff-section-edit-form">
      {error ? <p className="form-error">{error}</p> : null}

      <div className="resident-form-field full-span">
        <strong>Current designations</strong>
      </div>

      {roles.length ? (
        roles.map((role) => (
          <div className="resident-form-field full-span" key={role.code}>
            <span>
              <em className="pill blue">{ROLE_DEFINITIONS[role.code].label}</em>
              {role.assignedOn ? (
                <small> Since {formatRoleDate(role.assignedOn)}</small>
              ) : null}
            </span>
            <small>{ROLE_DEFINITIONS[role.code].description}</small>
            <div>
              <button
                className="mini-btn"
                type="button"
                disabled={saving}
                onClick={() => confirmRemove(role.code)}
              >
                <i className="ti ti-user-minus" /> Remove
              </button>
            </div>
          </div>
        ))
      ) : (
        <p>{personName || "This staff member"} has no designations yet.</p>
      )}

      {holdsComplianceChecked && !profileStatusCounts(careProfile) ? (
        <p>
          <i className="ti ti-info-circle" /> Onboarding isn't complete.
          Compliance checks only count a care profile once onboarding is
          complete, so the resident manager designation won't be picked up yet.
        </p>
      ) : null}

      {availableOptions.length ? (
        <>
          <div className="resident-form-field full-span">
            <strong>Add a designation</strong>
          </div>

          <label className="resident-form-field full-span">
            Designation
            <Select
              showSearch
              allowClear
              placeholder="Select a designation"
              value={selectedCode}
              options={availableOptions}
              optionFilterProp="label"
              disabled={saving}
              onChange={(code) => (code ? selectCode(code) : clearSelection())}
              style={{ width: "100%" }}
            />
          </label>

          {selectedCode ? (
            <>
              <small>{ROLE_DEFINITIONS[selectedCode].description}</small>
              <label className="resident-form-field">
                When did the{" "}
                {ROLE_DEFINITIONS[selectedCode].label.toLowerCase()} designation
                start?
                <input
                  type="date"
                  value={assignedOn}
                  max={todayIso()}
                  onChange={(event) => setAssignedOn(event.target.value)}
                />
              </label>
              <div className="care-staff-section-edit-actions">
                <button
                  className="mini-btn"
                  type="button"
                  onClick={clearSelection}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="mini-btn care-profile-primary-action"
                  type="button"
                  onClick={assign}
                  disabled={saving}
                >
                  <i className="ti ti-user-check" />{" "}
                  {saving ? "Saving..." : "Add designation"}
                </button>
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Props:
 *  - open / onClose
 *  - careProfile: the care profile response (id, status and the profile data JSON)
 *  - personName: shown in the title and confirmations
 *  - onChange(updatedProfile): called with the saved care profile response
 */
export default function CareProfileRolesDrawer({
  open,
  onClose,
  careProfile,
  personName,
  onChange,
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={personName ? `Designations for ${personName}` : "Designations"}
      width="min(520px, 100vw)"
      className="olreadia-flow-drawer care-staff-section-edit-drawer"
      destroyOnHidden
    >
      {open && careProfile ? (
        <RolesForm
          careProfile={careProfile}
          personName={personName}
          onChange={onChange}
        />
      ) : null}
    </Drawer>
  );
}
